/** @format */
/**
 * eslint-disable max-len
 */

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ProductSku } from '@shared/data-access/interfaces';
import { CookieService, StoreDao } from '@shared/data-access/services';

import { ProductService } from '@shared/data-access/product';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import {
	changeHeaderServiceStoreInfo,
	selectUserZipCode,
	setHeaderServiceStoreInfo,
	setNearbyStores,
	setUserZipCode
} from '@state/data-access';
import { selectAllProductSkusPartNumber, selectResolvedSku } from './product-selection.selectors';
import {
	eddInfoFetched,
	eddInfoFetchErrored,
	fetchSDDInfo,
	fetchSDDInfoError,
	fetchSDDInfoSuccess,
	skuResolutionActions
} from './product-selection.actions';

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { SameDayDeliveryService } from 'libs/pdp/data-access-new/services/src/lib/same-day-delivery.service';
import { selectKillswitch } from './killswitches/killswitches.selectors';

export const SDD_INVENTORY_ERROR_MESSAGE = 'Failed to fetch SDD inventory information';

@Injectable()
export class HeaderLocationEffects {
	fetchSDDInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fetchSDDInfo),
			filter(() => isPlatformBrowser(this.platformId)),
			switchMap(({ zipCode, skuId }) =>
				this.sameDayDeliveryService.getSameDayDeliveryApi$(zipCode, skuId).pipe(
					map((data) => fetchSDDInfoSuccess({ sdd: data.skuAvailability[0] })),
					catchError((error) =>
						of(
							fetchSDDInfoError({
								error: error?.message || SDD_INVENTORY_ERROR_MESSAGE
							})
						)
					)
				)
			)
		)
	);

	setSDDInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setUserZipCode, ...skuResolutionActions),
			concatLatestFrom(() => [
				this.store.select(selectResolvedSku),
				this.store.select(selectUserZipCode)
			]),
			switchMap(
				(
					info: [
						ReturnType<typeof setUserZipCode | (typeof skuResolutionActions)[number]>,
						ProductSku | null,
						string
					]
				) =>
					this.getSDDInfo(info).pipe(
						map((data) => fetchSDDInfoSuccess({ sdd: data.skuAvailability[0] })),
						catchError((error) =>
							of(
								fetchSDDInfoError({
									error: error?.message || SDD_INVENTORY_ERROR_MESSAGE
								})
							)
						)
					)
			)
		)
	);

	getEDDInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(...skuResolutionActions),
			// Since we need cookies from document object for setSkuDeliveryDate API call, only run this on the browser
			filter(() => isPlatformBrowser(this.platformId)),
			switchMap((info) =>
				this.setSkuDeliveryDate(info).pipe(
					map((data) => eddInfoFetched({ eddInfo: data })),
					catchError((error) => of(eddInfoFetchErrored({ error: error.message })))
				)
			)
		)
	);

	getNearbyStores$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setHeaderServiceStoreInfo, changeHeaderServiceStoreInfo),
			concatLatestFrom(() => this.store.select(selectAllProductSkusPartNumber)),
			switchMap(
				(
					stateData: [
						ReturnType<
							typeof setHeaderServiceStoreInfo | typeof changeHeaderServiceStoreInfo
						>,
						string[]
					]
				) =>
					this.getStoreInfo(stateData).pipe(
						map((data) => setNearbyStores({ nearbyStores: data })),
						catchError(() => of(setNearbyStores({ nearbyStores: null })))
					)
			)
		)
	);
	sameDayDeliveryEnabled = this.store.selectSignal(selectKillswitch('sameDayDeliveryEnabled'));
	skuEDDEnabled = this.store.selectSignal(selectKillswitch('skuEDDEnabled'));

	constructor(
		private actions$: Actions,
		private store: Store,
		public sameDayDeliveryService: SameDayDeliveryService,
		private productService: ProductService,
		private storeDao: StoreDao,
		private cookieService: CookieService,
		@Inject(PLATFORM_ID) private readonly platformId
	) {}

	public getSDDInfo(
		data: [
			ReturnType<typeof setUserZipCode | (typeof skuResolutionActions)[number]>,
			ProductSku | null,
			string
		]
	) {
		const resolvedSku = data[1];
		const zipCode = data[2];
		if (resolvedSku && zipCode && this.sameDayDeliveryEnabled()) {
			return this.sameDayDeliveryService.getSameDayDeliveryApi$(
				zipCode,
				Number(resolvedSku.partNumber)
			);
		} else {
			return throwError(() => new Error(SDD_INVENTORY_ERROR_MESSAGE));
		}
	}

	private setSkuDeliveryDate(info: ReturnType<(typeof skuResolutionActions)[number]>) {
		const hasDeliveryZipCookie =
			this.cookieService.check('deliveryZip') &&
			this.cookieService.get('deliveryZip') &&
			this.cookieService.get('deliveryZip') !== 'null';
		const hasWhereaboutsCookie =
			this.cookieService.check('whereabouts') &&
			this.cookieService.get('whereabouts') &&
			this.cookieService.get('whereabouts') !== 'null';
		const noZipCookieSet = !hasDeliveryZipCookie && !hasWhereaboutsCookie;
		if (noZipCookieSet) {
			return throwError(() => new Error('Missing Zip Code Cookie'));
		}

		if (info.resolvedSku && this.skuEDDEnabled()) {
			return this.productService.getSkuDeliveryDate$(info.resolvedSku.partNumber);
		} else {
			return of(null);
		}
	}

	public getStoreInfo(
		info: [
			ReturnType<typeof setHeaderServiceStoreInfo | typeof changeHeaderServiceStoreInfo>,
			string[]
		]
	) {
		const zipcode = info[0]?.storeInfo?.zipcode
			? info[0].storeInfo.zipcode
			: info[0]?.storeInfo?.zip;
		if (!info[1] || info[1].length === 0) {
			return of(null);
		}
		return this.storeDao.getStoreInformation$(zipcode?.split('-')[0], info[1], 1, true, 1).pipe(
			map((response) => {
				let storeDataBySku = {};
				if (info[1]) {
					info[1].forEach((singleSku) => {
						storeDataBySku = {
							...storeDataBySku,
							[singleSku]: []
						};
						response.data.results.forEach((storeResult) => {
							const skuInventory = storeResult.skus.findIndex(
								(s) => s.sku === singleSku
							);
							const { store: storeRes, distance, units } = storeResult;
							if (
								parseInt(storeResult.skus[skuInventory].qty.ats, 10) > 0 ||
								parseInt(storeResult.skus[skuInventory].qty.bopl, 10) > 0 ||
								parseInt(storeResult.skus[skuInventory].qty.isa, 10) > 0
							) {
								storeDataBySku[singleSku].push({
									storeRes,
									distance,
									units,
									inventory: storeResult.skus[skuInventory].qty
								});
							}
						});
					});
					return storeDataBySku;
				}
			})
		);
	}
}
